import { Link, Navigate } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { buttonClass } from "../../components/ui/Button";
import { DocTable } from "../../components/ui/DocTable";
import { EmptyState, PageTitle, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { groupByAudience, groupByType } from "../../features/tree/doc-groups";
import type { Space } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { useSpaces, useTree } from "../../lib/queries";

/**
 * One topic (an internal space) or the Templates space: its documents as a table, and one small "New" button
 * beside the title (page frame spec §5.1). A topic lists its top-level documents by type (document types spec
 * §4.2). Templates lists them by who they are written for: Klarity's own people, or a client.
 */
export function SpaceView({ space }: { space: Space }) {
  return (
    <>
      <PageBar>
        {space.kind === "templates" ? null : (
          <Crumb>
            <Link to="/handbook">Handbook</Link>
          </Crumb>
        )}
        <Crumb current>{space.name}</Crumb>
      </PageBar>
      <Page>
        <SpaceBody space={space} />
      </Page>
    </>
  );
}

/** The title, "New" button and document table, without a page bar: the admin layout supplies its own. */
export function SpaceBody({ space }: { space: Space }) {
  const me = useMe();
  const { data: docs = [], isLoading } = useTree(space.id);
  const templates = space.kind === "templates";
  const groups = templates ? groupByAudience(docs) : groupByType(docs);
  const canCreate = templates ? can(me, "admin") : can(me, "create");
  return (
    <>
      <PageTitle
        eyebrow={
          space.restricted ? (
            <span className="inline-flex items-center gap-1">
              <Lock size={11} /> {templates ? "Restricted" : "Restricted topic"}
            </span>
          ) : undefined
        }
        aside={
          canCreate ? (
            <Link to="/new" search={{ space: space.id }} className={buttonClass({ size: "sm" })}>
              {templates ? "New template" : "New document"}
            </Link>
          ) : undefined
        }
      >
        {space.name}
      </PageTitle>
      {isLoading ? <Spinner label="Loading documents…" /> : null}
      {!isLoading && groups.length === 0 ? (
        <EmptyState
          title={templates ? "No templates yet" : "This topic is empty"}
          body={templates ? undefined : "Start a document from a template, or a plain wiki page."}
        />
      ) : null}
      {/* A topic with one kind of document needs no group row. Templates always says who its templates are for. */}
      {templates ? (
        <DocTable label="Templates" groups={groups} titleHeading="Template" showType headings="always" />
      ) : (
        <DocTable label={`${space.name} documents`} groups={groups} titleHeading="Document" headings="auto" />
      )}
    </>
  );
}

export function SpacePage({ slug }: { slug: string }) {
  const { data: spaces, isLoading } = useSpaces();
  const space = spaces?.find((s) => s.slug === slug);
  if (isLoading)
    return (
      <>
        <PageBar>
          <Crumb current>Topic</Crumb>
        </PageBar>
        <Page>
          <Spinner label="Loading…" />
        </Page>
      </>
    );
  if (!space)
    return (
      <>
        <PageBar>
          <Crumb current>Topic</Crumb>
        </PageBar>
        <Page>
          <EmptyState title="This topic does not exist or you don't have access to it" />
        </Page>
      </>
    );
  // A client's space is its binder.
  if (space.kind === "client") return <Navigate to="/clients/$clientId" params={{ clientId: space.id }} replace />;
  return <SpaceView space={space} />;
}
